import { useEffect, useRef, useState } from "react";

import type { TextOverlay } from "../../api/projects";
import { useEditor } from "../../store/editor";

/**
 * Capa DOM sobre el canvas WebGL para renderizar textos.
 *
 * Computa el rectángulo real de display del canvas (object-fit: contain)
 * dentro del panel-preview, y posiciona cada texto en porcentajes (x, y)
 * de ese rectángulo. Soporta selección, drag para reposicionar y fade in/out.
 */
export default function PreviewOverlays(props: {
  canvas: HTMLCanvasElement | null;
  videoWidth: number;
  videoHeight: number;
}) {
  const { canvas, videoWidth, videoHeight } = props;
  const timeline = useEditor((s) => s.timeline);
  const playhead = useEditor((s) => s.playhead_ms);
  const selectedId = useEditor((s) => s.selectedOverlayId);
  const selectOverlay = useEditor((s) => s.selectOverlay);
  const updateOverlay = useEditor((s) => s.updateOverlay);

  const containerRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ left: number; top: number; w: number; h: number } | null>(null);
  const [drag, setDrag] = useState<{ id: string; startX: number; startY: number; origX: number; origY: number } | null>(null);

  // Recalcular el rect de display del canvas al cambiar tamaño del panel
  useEffect(() => {
    if (!canvas || !containerRef.current) return;
    const parent = containerRef.current.parentElement;
    if (!parent) return;
    function recompute() {
      const pr = parent!.getBoundingClientRect();
      const aR = videoWidth / Math.max(1, videoHeight);
      const cR = pr.width / Math.max(1, pr.height);
      let w, h, l, t;
      if (cR > aR) {
        h = pr.height; w = h * aR; l = (pr.width - w) / 2; t = 0;
      } else {
        w = pr.width; h = w / aR; l = 0; t = (pr.height - h) / 2;
      }
      setRect({ left: l, top: t, w, h });
    }
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [canvas, videoWidth, videoHeight]);

  // Drag handlers
  useEffect(() => {
    if (!drag || !rect) return;
    function onMove(e: MouseEvent) {
      const dxRel = (e.clientX - drag!.startX) / rect!.w;
      const dyRel = (e.clientY - drag!.startY) / rect!.h;
      const nx = Math.max(0, Math.min(1, drag!.origX + dxRel));
      const ny = Math.max(0, Math.min(1, drag!.origY + dyRel));
      updateOverlay(drag!.id, { x: nx, y: ny });
    }
    function onUp() { setDrag(null); }
    document.body.style.cursor = "grabbing";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [drag, rect, updateOverlay]);

  function startDrag(e: React.MouseEvent, ov: TextOverlay) {
    e.stopPropagation();
    selectOverlay(ov.id);
    setDrag({ id: ov.id, startX: e.clientX, startY: e.clientY, origX: ov.x, origY: ov.y });
  }

  if (!rect) {
    return <div ref={containerRef} style={{ position: "absolute", inset: 0, pointerEvents: "none" }} />;
  }

  const overlays = (timeline.overlays || []) as TextOverlay[];
  const scale = rect.h / 1080;

  /** Easing: ease-out (más natural para slides/zooms de entrada) */
  function easeOut(t: number): number { return 1 - Math.pow(1 - t, 3); }
  function easeInOut(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /** Calcula alpha + transform a partir del estado de animación in/out. */
  function animState(o: TextOverlay): {
    alpha: number;
    transform: string;
    typedChars?: number;
  } {
    if (playhead < o.start || playhead > o.end) return { alpha: 0, transform: "" };
    const fadeIn = o.fade_in_ms || 0;
    const fadeOut = o.fade_out_ms || 0;
    const tIn = playhead - o.start;
    const tOut = o.end - playhead;
    const animIn = o.animation_in || "fade";
    const animOut = o.animation_out || "fade";

    let alpha = 1;
    const transforms: string[] = [];
    let typedChars: number | undefined;

    // Animación de entrada
    if (fadeIn > 0 && tIn < fadeIn) {
      const p = easeOut(tIn / fadeIn);   // 0..1
      switch (animIn) {
        case "fade": alpha = p; break;
        case "slide-left":  alpha = 1; transforms.push(`translateX(${(p - 1) * 200}px)`); break;
        case "slide-right": alpha = 1; transforms.push(`translateX(${(1 - p) * 200}px)`); break;
        case "slide-up":    alpha = 1; transforms.push(`translateY(${(1 - p) * 200}px)`); break;
        case "slide-down":  alpha = 1; transforms.push(`translateY(${(p - 1) * 200}px)`); break;
        case "zoom":  alpha = p; transforms.push(`scale(${0.4 + p * 0.6})`); break;
        case "pop": {
          // overshoot: scale 0 → 1.15 → 1
          const k = p < 0.7 ? p / 0.7 : 1;
          const s = p < 0.7 ? 1.15 * easeOut(p / 0.7) : 1.15 - (p - 0.7) / 0.3 * 0.15;
          alpha = k; transforms.push(`scale(${s})`); break;
        }
        case "typewriter": {
          typedChars = Math.max(1, Math.ceil((o.text || "").length * p));
          alpha = 1;
          break;
        }
        case "none": default: alpha = 1;
      }
    }

    // Animación de salida (sólo si no estamos ya en in)
    if (fadeOut > 0 && tOut < fadeOut && tIn >= fadeIn) {
      const p = easeInOut(1 - tOut / fadeOut);   // 0..1
      switch (animOut) {
        case "fade": alpha = Math.min(alpha, 1 - p); break;
        case "slide-left":  transforms.push(`translateX(${-p * 200}px)`); break;
        case "slide-right": transforms.push(`translateX(${p * 200}px)`); break;
        case "slide-up":    transforms.push(`translateY(${-p * 200}px)`); break;
        case "slide-down":  transforms.push(`translateY(${p * 200}px)`); break;
        case "zoom": alpha = Math.min(alpha, 1 - p); transforms.push(`scale(${1 - p * 0.6})`); break;
        case "pop":  alpha = Math.min(alpha, 1 - p); transforms.push(`scale(${1 + p * 0.2})`); break;
        case "none": default: break;
      }
    }

    return { alpha: Math.max(0, Math.min(1, alpha)), transform: transforms.join(" "), typedChars };
  }

  return (
    <div
      ref={containerRef}
      onMouseDown={(e) => {
        // click en zona vacía = quitar selección
        if (e.target === e.currentTarget) selectOverlay(null);
      }}
      style={{
        position: "absolute",
        left: rect.left, top: rect.top,
        width: rect.w, height: rect.h,
        pointerEvents: "auto",
        overflow: "hidden",
      }}
    >
      {overlays.map((o) => {
        const visible = playhead >= o.start && playhead <= o.end;
        if (!visible && selectedId !== o.id) return null;
        const anim = visible ? animState(o) : { alpha: 0.5, transform: "", typedChars: undefined };
        const isSel = selectedId === o.id;
        const px = o.x * rect.w;
        const py = o.y * rect.h;
        const fs = (o.font_size || 64) * scale;
        const align = o.align || "center";
        // anchor según alineación + animaciones
        const baseAnchor =
          align === "center" ? "translate(-50%, -50%)"
          : align === "left" ? "translate(0, -50%)"
          : "translate(-100%, -50%)";
        const combinedTransform = `${baseAnchor} ${anim.transform || ""}`.trim();

        // typewriter: recortar texto a typedChars
        const fullText = o.text || " ";
        const displayText = anim.typedChars !== undefined
          ? fullText.slice(0, anim.typedChars)
          : fullText;

        return (
          <div
            key={o.id}
            onMouseDown={(e) => startDrag(e, o)}
            style={{
              position: "absolute",
              left: px, top: py,
              transform: combinedTransform,
              transformOrigin: "center center",
              padding: (o.padding ?? 0) * scale,
              maxWidth: rect.w - 20,
              background: o.background || "transparent",
              color: o.color || "#ffffff",
              fontSize: fs,
              fontWeight: o.bold ? 700 : 400,
              textAlign: align,
              fontFamily: "ui-sans-serif, system-ui, sans-serif",
              opacity: anim.alpha,
              cursor: "grab",
              outline: isSel ? "2px dashed var(--accent)" : "none",
              outlineOffset: 4,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.15,
              borderRadius: 4,
              userSelect: "none",
            }}
            title="Arrastra para mover"
          >
            {displayText}
          </div>
        );
      })}
    </div>
  );
}
